/**
 * Admin: /admin/ops/ai/agents — Global Agents Health Dashboard — FASE 9
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Bot, RefreshCw, Activity, AlertCircle, Users, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/ops/ai/agents")({
  component: AdminAgentsPage,
  head: () => ({ meta: [{ title: "Agents — AI Control — Admin" }] }),
});

interface GlobalStats {
  total_runs: number;
  total_cost_microcents: number;
  total_errors: number;
  active_tenants: number;
  top_agents: Array<{ agent_id: string; count: number }>;
}

interface RegistryAgent {
  id: string;
  name: string;
  domain: string;
  skillIds: string[];
  priority: number;
  analyticsTags: string[];
}

function StatCard({ label, value, icon: Icon, accent }: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  accent?: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
      <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg", accent ?? "bg-muted")}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-xl font-bold tabular-nums">{value}</div>
        <div className="text-[11px] text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function AdminAgentsPage() {
  const [registry, setRegistry] = useState<RegistryAgent[]>([]);
  const [global, setGlobal] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/ai/agents", { credentials: "include" });
      const data = (await res.json()) as { registry: RegistryAgent[]; global: GlobalStats };
      setRegistry(data.registry ?? []);
      setGlobal(data.global ?? null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Bot className="h-4 w-4 text-violet-500" />
            Agentes — Global Health
          </h2>
          <p className="text-xs text-muted-foreground">
            Últimos 7 días · {registry.length} agentes en registry
          </p>
        </div>
        <Button size="sm" variant="outline" className="gap-2" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Stats */}
          {global && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Runs (7d)" value={global.total_runs} icon={Activity} accent="bg-blue-100 text-blue-600 dark:bg-blue-900/30" />
              <StatCard label="Tenants activos" value={global.active_tenants} icon={Users} accent="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30" />
              <StatCard label="Errores (7d)" value={global.total_errors} icon={AlertCircle} accent="bg-red-100 text-red-600 dark:bg-red-900/30" />
              <StatCard
                label="Costo total (µ¢)"
                value={global.total_cost_microcents.toLocaleString()}
                icon={TrendingUp}
                accent="bg-violet-100 text-violet-600 dark:bg-violet-900/30"
              />
            </div>
          )}

          {/* Top agents */}
          {global && global.top_agents.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                Top agents (7d)
              </p>
              <div className="flex flex-wrap gap-2">
                {global.top_agents.map((a) => (
                  <div key={a.agent_id} className="rounded-lg border bg-muted/30 px-3 py-1.5 flex items-center gap-2">
                    <Bot className="h-3.5 w-3.5 text-violet-500" />
                    <span className="text-sm font-medium">{a.agent_id}</span>
                    <Badge variant="secondary" className="text-[10px]">{a.count}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Registry table */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
              Registry ({registry.length})
            </p>
            <div className="rounded-xl border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Agent</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Dominio</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Skills</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Prioridad</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {registry.map((agent) => (
                    <tr key={agent.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <Bot className="h-3.5 w-3.5 text-violet-400 shrink-0" />
                          <span className="font-medium">{agent.name}</span>
                          <span className="text-[10px] text-muted-foreground">({agent.id})</span>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant="outline" className="text-[10px]">{agent.domain}</Badge>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {agent.skillIds.map((s) => (
                            <Badge key={s} variant="secondary" className="text-[10px] py-0">{s}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                        {agent.priority}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
